/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import apiClient from '../api/client';

const AuthContext = createContext(null);

// ── Branding constants ──────────────────────────────────────────────────────
const DAISY_THEMES = [
  "light", "dark", "cupcake", "bumblebee", "emerald", "corporate", "synthwave",
  "retro", "cyberpunk", "valentine", "halloween", "garden", "forest", "aqua",
  "lofi", "pastel", "fantasy", "wireframe", "black", "luxury", "dracula",
  "cmyk", "autumn", "business", "acid", "lemonade", "night", "coffee", "winter",
  "dim", "nord", "sunset", "caramellatte",
];
const VALID_THEMES = new Set(DAISY_THEMES);

function validateTheme(theme) {
  if (theme && VALID_THEMES.has(theme)) return theme;
  if (theme) console.warn(`[AuthContext] Invalid theme "${theme}", falling back to "light"`);
  return 'light';
}

function validateLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  console.warn('[AuthContext] Invalid logo URL protocol, falling back to default');
  return null;
}

function extractBranding(userData) {
  return {
    logoUrl: validateLogoUrl(userData?.subAccountLogoUrl ?? null),
    faviconUrl: validateLogoUrl(userData?.subAccountFaviconUrl ?? null),
    theme: validateTheme(userData?.subAccountTheme ?? null),
  };
}

// ── Storage helpers ─────────────────────────────────────────────────────────
const clearStorage = () => {
  localStorage.removeItem('auth_access_token');
  localStorage.removeItem('auth_refresh_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_account_name');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accountName, setAccountName] = useState(null);
  const [userScope, setUserScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('light');

  // Apply favicon to document
  useEffect(() => {
    const url = currentFaviconUrl || '/favicon.ico';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }, [currentFaviconUrl]);

  // Apply theme to document and persist (only when NOT in sub-account context)
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
    if (!user?.subAccountUID) {
      try {
        localStorage.setItem('app-theme', currentTheme);
      } catch (e) {
        console.warn('[AuthContext] Could not persist theme to localStorage:', e);
      }
    }
  }, [currentTheme, user?.subAccountUID]);

  // Restore auth state and branding from localStorage on mount
  useEffect(() => {
    const restore = async () => {
      try {
        const savedToken = localStorage.getItem('auth_access_token');
        const savedUser = localStorage.getItem('auth_user');
        if (savedToken && savedUser) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setUserScope(parsedUser?.scope || 'platform');
          setAccountName(localStorage.getItem('auth_account_name'));
          const { logoUrl, faviconUrl, theme } = extractBranding(parsedUser);
          setCurrentLogoUrl(logoUrl);
          setCurrentFaviconUrl(faviconUrl);
          setCurrentTheme(theme);
        }
      } catch {
        clearStorage();
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, []);

  const login = useCallback(async (email, password, subAccountUID, slug) => {
    const { data } = await apiClient.post('/auth/login', {
      email,
      password,
      ...(subAccountUID && { subAccountUID }),
      ...(slug && { slug }),
    });
    const { accessToken, refreshToken } = data;

    localStorage.setItem('auth_access_token', accessToken);
    localStorage.setItem('auth_refresh_token', refreshToken);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    const { data: userData } = await apiClient.get('/auth/me');
    const scope = userData?.scope || 'platform';
    const userWithScope = { ...userData, scope };

    localStorage.setItem('auth_user', JSON.stringify(userWithScope));
    setUser(userWithScope);
    setUserScope(scope);
    setAccountName(null);

    const { logoUrl, faviconUrl, theme } = extractBranding(userData);
    setCurrentLogoUrl(logoUrl);
    setCurrentFaviconUrl(faviconUrl);
    setCurrentTheme(theme);

    return userWithScope;
  }, []);

  const switchContext = useCallback(async (accountUID, subAccountUID) => {
    const { data } = await apiClient.post('/auth/switch-context', {
      accountUID,
      ...(subAccountUID && { subAccountUID }),
    });

    localStorage.setItem('auth_access_token', data.accessToken);
    localStorage.setItem('auth_account_name', data.accountName);
    localStorage.setItem('auth_refresh_token', data.refreshToken);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

    const { data: userData } = await apiClient.get('/auth/me');
    const scope = data.scope || userData?.scope || 'platform';
    const userWithScope = { ...userData, scope };

    localStorage.setItem('auth_user', JSON.stringify(userWithScope));
    setUser(userWithScope);
    setUserScope(scope);
    setAccountName(data.accountName);

    const { logoUrl, faviconUrl, theme } = extractBranding(userData);
    setCurrentLogoUrl(logoUrl);
    setCurrentFaviconUrl(faviconUrl);
    setCurrentTheme(theme);

    return data;
  }, []);

  const resetContext = useCallback(async () => {
    const { data } = await apiClient.post('/auth/switch-context', {});

    localStorage.setItem('auth_access_token', data.accessToken);
    if (data.refreshToken) { localStorage.setItem('auth_refresh_token', data.refreshToken); }
    localStorage.removeItem('auth_account_name');
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

    const { data: userData } = await apiClient.get('/auth/me');
    const scope = userData?.scope || 'platform';
    const userWithScope = { ...userData, scope };

    localStorage.setItem('auth_user', JSON.stringify(userWithScope));
    setUser(userWithScope);
    setUserScope(scope);
    setAccountName(null);

    // Leaving sub-account context: clear sub-account logo/favicon and restore saved theme
    setCurrentLogoUrl(null);
    setCurrentFaviconUrl(null);
    const savedTheme = validateTheme(localStorage.getItem('app-theme'));
    setCurrentTheme(savedTheme);
  }, []);

  const updateProfile = useCallback((userData) => {
    const updatedUser = { ...user, ...userData };
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  }, [user]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('auth_refresh_token');
      if (refreshToken) {
        await apiClient.post('/auth/logout', {}, {
          headers: {
            Authorization: `Bearer ${refreshToken}`
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      clearStorage();
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      setUserScope(null);
      setAccountName(null);
      setCurrentLogoUrl(null);
      setCurrentFaviconUrl(null);
      setCurrentTheme('light');
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      userScope,
      accountName,
      currentLogoUrl,
      currentFaviconUrl,
      currentTheme,
      setCurrentTheme,
      login,
      switchContext,
      resetContext,
      logout,
      updateProfile,
      loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
